import { useState, useEffect, useMemo, useCallback, createContext, useContext, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { updateLocaleCache } from '../utils/locale';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Deriving the BCP-47 locale from tenant global settings.
 * Priority:
 *   1. explicit `settings.locale`
 *   2. `settings.language` + `settings.country`  → `en-US`, `en-IN`, `de-DE`
 *   3. `settings.language` alone                 → `en`, `de`, `ar`
 *   4. Browser locale                             → `navigator.language`
 */
function deriveLocale(settings) {
  if (!settings) return 'en-US';
  if (settings.locale) return settings.locale;
  const lang = (settings.language || '').toLowerCase().split('-')[0];
  const country = (settings.country || '').toUpperCase();
  if (lang && country) return `${lang}-${country}`;
  if (lang) return lang;
  return typeof navigator !== 'undefined' ? navigator.language : 'en-US';
}

/**
 * Map backend `date_format` template (MM/DD/YYYY, DD/MM/YYYY, YYYY-MM-DD, etc.)
 * to an `Intl.DateTimeFormat` options object. The locale still drives things
 * like month names and RTL, but the *pattern* is honored per tenant preference.
 */
function dateOptsForFormat(fmt = 'MM/DD/YYYY') {
  const F = fmt.toUpperCase();
  if (F.startsWith('YYYY')) return { year: 'numeric', month: '2-digit', day: '2-digit' };
  // Default 2-digit for everything else; locale + Intl chooses the DMY vs MDY ordering
  return { year: 'numeric', month: '2-digit', day: '2-digit' };
}

const DEFAULT_STATE = {
  code: null,             // e.g. 'USD', 'INR', 'EUR' — null until fetched
  symbol: null,           // e.g. '$', '₹', '€'
  position: 'before',
  decimals: 2,
  locale: 'en-US',
  language: 'en',
  country: '',
  timezone: 'America/New_York',
  dateFormat: 'MM/DD/YYYY',
  fiscalYearStart: 'january',
  ready: false,           // true once /api/settings/global returns AND currency.code exists
  confirmed: false,       // user has explicitly saved GlobalSettings
  loading: true,
  error: null,
  // formatters (installed by provider)
  format: (n) => `${n ?? 0}`,
  formatCompact: (n) => `${n ?? 0}`,
  formatNumber: (n) => `${n ?? 0}`,
  formatDate: (d) => (d ? String(d) : ''),
  formatDateTime: (d) => (d ? String(d) : ''),
  formatTime: (d) => (d ? String(d) : ''),
  refresh: async () => {},
};

const CurrencyContext = createContext(DEFAULT_STATE);

export function CurrencyProvider({ children }) {
  const { user } = useAuth() || {};
  const [state, setState] = useState(DEFAULT_STATE);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    // Cancel any in-flight fetch
    try { abortRef.current?.abort?.(); } catch { /* ignore */ }
    const controller = new AbortController();
    abortRef.current = controller;

    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await axios.get(`${API}/api/settings/global`, { signal: controller.signal });
      const c = data?.currency || {};
      const code = c.code || null;
      const confirmed = !!data?.currency_confirmed;
      const locale = deriveLocale(data);
      const decimals = c.decimal_places ?? 2;
      updateLocaleCache({
        code,
        locale,
        decimals,
        symbol: c.symbol || null,
        timezone: data?.timezone || 'America/New_York',
        dateFormat: data?.date_format || 'MM/DD/YYYY',
      });
      setState(s => ({
        ...s,
        code,
        symbol: c.symbol || null,
        position: c.position || 'before',
        decimals,
        locale,
        language: data?.language || 'en',
        country: data?.country || '',
        timezone: data?.timezone || 'America/New_York',
        dateFormat: data?.date_format || 'MM/DD/YYYY',
        fiscalYearStart: data?.fiscal_year_start || 'january',
        ready: !!code,
        confirmed,
        loading: false,
        error: null,
      }));
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      setState(s => ({ ...s, ready: false, confirmed: false, loading: false, error: err?.message || 'Failed to load settings' }));
    }
  }, []);

  // Fetch on mount and whenever the authenticated user changes (login/logout/impersonate).
  useEffect(() => {
    if (!user) {
      // Reset to unready when logged out so protected screens don't render stale currency.
      setState(s => ({ ...s, ready: false, confirmed: false, loading: false }));
      return;
    }
    load();
    return () => { try { abortRef.current?.abort?.(); } catch { /* ignore */ } };
  }, [user, load]);

  // ─── Formatters ──────────────────────────────────────────────────────────────
  const format = useCallback((amount) => {
    const n = Number(amount || 0);
    if (!state.ready || !state.code) return '—';
    try {
      return new Intl.NumberFormat(state.locale, {
        style: 'currency',
        currency: state.code,
        minimumFractionDigits: state.decimals,
        maximumFractionDigits: state.decimals,
      }).format(n);
    } catch {
      // Bad code shouldn't crash the app
      return `${state.symbol || ''}${n.toFixed(state.decimals)}`;
    }
  }, [state.ready, state.code, state.locale, state.decimals, state.symbol]);

  const formatCompact = useCallback((amount) => {
    const n = Number(amount || 0);
    if (!state.ready || !state.code) return '—';
    try {
      return new Intl.NumberFormat(state.locale, {
        style: 'currency',
        currency: state.code,
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(n);
    } catch {
      return format(n);
    }
  }, [state.ready, state.code, state.locale, format]);

  const formatNumber = useCallback((n, opts = {}) => {
    try {
      return new Intl.NumberFormat(state.locale, opts).format(Number(n || 0));
    } catch {
      return `${n ?? 0}`;
    }
  }, [state.locale]);

  const formatDate = useCallback((d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(state.locale, {
        ...dateOptsForFormat(state.dateFormat),
        timeZone: state.timezone,
      }).format(dt);
    } catch {
      return dt.toLocaleDateString();
    }
  }, [state.locale, state.dateFormat, state.timezone]);

  const formatDateTime = useCallback((d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(state.locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: state.timezone,
      }).format(dt);
    } catch {
      return dt.toLocaleString();
    }
  }, [state.locale, state.timezone]);

  const formatTime = useCallback((d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(state.locale, {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: state.timezone,
      }).format(dt);
    } catch {
      return dt.toLocaleTimeString();
    }
  }, [state.locale, state.timezone]);

  const value = useMemo(() => ({
    ...state,
    format,
    formatCompact,
    formatNumber,
    formatDate,
    formatDateTime,
    formatTime,
    refresh: load,
  }), [state, format, formatCompact, formatNumber, formatDate, formatDateTime, formatTime, load]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
